import type { Round, Tournament, Player } from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';

type DerivedInput = {
  tournament: Tournament | null;
  rounds: Round[];
  selectedRound: number;
  players: Player[];
  godMode: boolean;
  noValidPairing: boolean;
};

export function useRoundDerived({
  tournament,
  rounds,
  selectedRound,
  players,
  godMode,
  noValidPairing,
}: DerivedInput) {
  const currentRound = rounds[selectedRound] || null;
  const isKo = tournament?.system === '3ko' || tournament?.system === '5ko';
  const isRoundRobin =
    tournament?.system === 'round_robin' || tournament?.system === 'double_round_robin';

  // Polish Open 2026 incident: these used to look only at the SELECTED
  // round, so an earlier round left incomplete by a god-mode force-generate
  // was invisible — the generate button stayed active without god mode and
  // no incomplete-rounds indication showed. Check EVERY round.
  const anyIncompleteRound = rounds.some(
    (r) => r.status === 'published' || r.status === 'paired',
  );
  const canGenerate =
    tournament &&
    (tournament.status === 'registration_closed' ||
      tournament.status === 'in_progress') &&
    !anyIncompleteRound &&
    players.length >= 2;

  const hasIncompleteRounds =
    tournament &&
    (tournament.status === 'registration_closed' ||
      tournament.status === 'in_progress') &&
    anyIncompleteRound;

  const canUnpublish =
    currentRound?.status === 'published' &&
    currentRound.pairings
      .filter((p) => p.blackPlayer !== null)
      .every((p) => p.resultStatus === 'none');

  const allResultsConfirmed =
    currentRound?.pairings.every((p) => p.resultStatus === 'confirmed') ?? false;

  const allRoundsCompleted = rounds.length > 0 && rounds.every((r) => r.status === 'completed');
  const activePlayerCount = players.length;
  const allPlannedRoundsDone =
    tournament?.rounds != null && allRoundsCompleted && rounds.length >= tournament.rounds;
  const isOpenEnded = tournament?.rounds == null && !isKo;
  // Story 105 — KO qualifier finish: the field endpoint is koFinishTop
  // still-in players, not necessarily a single winner. Non-KO systems have
  // koFinishTop = 1, so this stays `<= 1` for them.
  const koFinishTop = tournament?.koFinishTop ?? 1;
  const canCompleteTournament =
    tournament?.status === 'in_progress' &&
    allRoundsCompleted &&
    (activePlayerCount <= koFinishTop ||
      allPlannedRoundsDone ||
      noValidPairing ||
      isOpenEnded ||
      godMode);

  const gameConfig = getGameConfig(tournament?.game);
  const resultOptions = gameConfig.resultOptions.map((r) => ({ value: r.value, label: r.label }));

  return {
    currentRound,
    isKo,
    isRoundRobin,
    canGenerate,
    hasIncompleteRounds,
    canUnpublish,
    allResultsConfirmed,
    allRoundsCompleted,
    activePlayerCount,
    allPlannedRoundsDone,
    isOpenEnded,
    canCompleteTournament,
    gameConfig,
    resultOptions,
  };
}
